import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getIpInfo } from '../services/ipService';
import { isValidIP, isValidDomain } from '../utils/helpers';
import { useDeviceType } from '../hooks/useDeviceType';
import IpCard from '../components/ip/IpCard';
import MapPreview from '../components/ip/MapPreview';

const IpInfoPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [ipData, setIpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customIp, setCustomIp] = useState('');
  const cancelTokenSource = useRef(null);
  const { isMobile, isTablet } = useDeviceType();

  const fetchIpInfo = async (ip = '') => {
    // 取消之前的请求
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel('取消之前的请求');
    }
    
    // 创建新的取消令牌
    cancelTokenSource.current = axios.CancelToken.source();
    
    setLoading(true);
    setError(null);
    
    const result = await getIpInfo(ip, cancelTokenSource.current.token);
    
    // 检查请求是否被取消
    if (result.errorCode === 'CANCELLED') {
      return;
    }
    
    if (result.success) {
      setIpData(result.data);
    } else {
      setError(result.errorMessage);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    const ipFromQuery = searchParams.get('ip');
    if (ipFromQuery) {
      setCustomIp(ipFromQuery);
      fetchIpInfo(ipFromQuery);
    } else {
      fetchIpInfo();
    }
  }, [searchParams]);

  // 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel('组件卸载');
      }
    };
  }, []);

  const handleCustomLookup = (e) => {
    e.preventDefault();
    const query = customIp.trim();
    if (!query) {
      setError(t('ipInfo.emptyInput'));
      return;
    }
    if (isValidIP(query)) {
      fetchIpInfo(query);
    } else if (isValidDomain(query)) {
      navigate(`/dns?domain=${encodeURIComponent(query)}`);
    } else {
      setError(t('ipInfo.invalidInput'));
    }
  };

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isMobile ? '1rem' : isTablet ? '1.5rem' : '2rem'
  };

  const titleStyle = {
    textAlign: 'center',
    color: colors.text,
    marginBottom: isMobile ? '1.5rem' : '2rem',
    fontSize: isMobile ? '2rem' : isTablet ? '2.2rem' : '2.5rem',
    fontWeight: '600',
    textShadow: 'none'
  };

  const searchFormStyle = {
    background: colors.cardBackground,
    padding: isMobile ? '1.5rem' : '2rem',
    borderRadius: '16px',
    marginBottom: isMobile ? '1.5rem' : '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)'
  };

  const inputGroupStyle = {
    display: 'flex',
    gap: isMobile ? '0.5rem' : '1rem',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'stretch' : 'center'
  };

  const inputStyle = {
    flex: 1,
    minWidth: isMobile ? 'auto' : '250px',
    padding: '0.75rem 1rem',
    border: `2px solid ${colors.inputBorder}`,
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'all 0.3s ease',
    background: colors.inputBackground,
    color: colors.text
  };

  const buttonStyle = {
    padding: '0.75rem 2rem',
    background: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
    width: isMobile ? '100%' : 'auto'
  };

  const contentGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile 
      ? '1fr' 
      : isTablet 
        ? 'repeat(auto-fit, minmax(300px, 1fr))' 
        : 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: isMobile ? '1.5rem' : '2rem',
    marginTop: isMobile ? '1.5rem' : '2rem'
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>{t('ipInfo.title')}</h1>
      
      <div style={searchFormStyle}>
        <h3 style={{ 
          marginBottom: '1rem', 
          color: colors.text,
          fontSize: isMobile ? '1rem' : '1.2rem'
        }}>{t('ipInfo.searchTitle')}</h3>
        <form onSubmit={handleCustomLookup} style={inputGroupStyle}>
          <input
            type="text"
            placeholder={t('ipInfo.placeholder')}
            value={customIp}
            onChange={(e) => setCustomIp(e.target.value)}
            style={inputStyle}
          />
          <button type="submit" style={buttonStyle}>
            {t('ipInfo.searchButton')}
          </button>
        </form>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: isMobile ? '1rem' : '1.2rem',
          padding: isMobile ? '1.5rem' : '2rem'
        }}>
          {t('ipInfo.loading')}
        </div>
      )}

      {error && (
        <div style={{
          background: colors.error,
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          textAlign: 'center',
          marginBottom: isMobile ? '1.5rem' : '2rem',
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          {t('common.error', { message: error })}
        </div>
      )}

      {ipData && !loading && (
        <div style={contentGridStyle}>
          <IpCard ipData={ipData} />
          <MapPreview ipData={ipData} />
        </div>
      )}
    </div>
  );
};

export default IpInfoPage;
